import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  TextField,
  Typography
} from '@mui/material';

import PrimaryButton from '../ui/buttons/primaryButton';
import { updateTeamStatus } from '../../features/team/teamSlice';
import { setEvent } from '../../features/event/eventSlice';

export const TEAM_STATUS = {
  ACTIVE: 'Active',
  WITHDRAWN: 'Withdrawn',
  DISQUALIFIED: 'Disqualified'
};

const TeamStatusDialog = ({ open, onClose, team, eventId }) => {
  const dispatch = useDispatch();
  const updating = useSelector((state) => state.team.updatingTeamStatus);
  const event = useSelector((state) => state.event.event);

  const [status, setStatus] = useState(TEAM_STATUS.ACTIVE);
  const [reason, setReason] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    if (team) {
      setStatus(team.status || TEAM_STATUS.ACTIVE);
      setReason(team.statusReason || '');
      setError('');
    }
  }, [team]);

  const handleClose = () => {
    setError('');
    onClose();
  };

  const handleSubmit = async () => {
    if (status !== TEAM_STATUS.ACTIVE && !reason.trim()) {
      setError('A reason is required.');
      return;
    }

    try {
      const result = await dispatch(
        updateTeamStatus({ eventId, teamId: team.id, status, reason: reason.trim() })
      ).unwrap();
      if (result?.teams && result?.players && event) {
        dispatch(setEvent({ ...event, teams: result.teams, players: result.players }));
      }
      handleClose();
    } catch (err) {
      setError(err?.message || 'Failed to update team status.');
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="xs">
      <DialogTitle>Team Status</DialogTitle>
      <DialogContent>
        {team && (
          <Box display="flex" flexDirection="column" gap={2} sx={{ pt: 1 }}>
            <Typography variant="subtitle2" fontWeight="bold">
              {team.name}
            </Typography>

            <TextField
              select
              fullWidth
              label="Status"
              value={status}
              disabled={updating}
              onChange={(event) => setStatus(event.target.value)}>
              <MenuItem value={TEAM_STATUS.ACTIVE}>Active</MenuItem>
              <MenuItem value={TEAM_STATUS.WITHDRAWN}>Withdrawn</MenuItem>
              <MenuItem value={TEAM_STATUS.DISQUALIFIED}>Disqualified</MenuItem>
            </TextField>

            {status !== TEAM_STATUS.ACTIVE && (
              <TextField
                fullWidth
                multiline
                minRows={2}
                label="Reason*"
                value={reason}
                disabled={updating}
                onChange={(event) => setReason(event.target.value)}
              />
            )}

            {error && (
              <Typography variant="caption" color="error">
                {error}
              </Typography>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={updating}>
          Cancel
        </Button>
        <PrimaryButton onClick={handleSubmit} variant="contained" disabled={updating}>
          Save Status
        </PrimaryButton>
      </DialogActions>
    </Dialog>
  );
};

export default TeamStatusDialog;
